import { PERMISSIONS } from '@helios/authz';
import { useScopedNumericQuery } from '@/hooks/useScopedNumericQuery';
import { financeCommandCenterApi } from './financeCommandCenter.api';
import type { FinanceInvoiceQuery, InvoiceDirection } from './financeInvoices.types';
import { analyticsClient } from '@/services/analyticsClient';
import { useSessionScopeFence } from '@/hooks/useSessionScope';

const invoiceRead = { ar: PERMISSIONS.FINANCE_AR_INVOICES_READ, ap: PERMISSIONS.FINANCE_AP_INVOICES_READ };

export function useFinanceCommandCenter() {
  return useScopedNumericQuery(['finance', 'command-center'], PERMISSIONS.FINANCE_BUDGETS_READ,
    async (options) => {
      const result = await financeCommandCenterApi.getDashboard(options);
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load finance command center');
      }
      return result.data;
    }, { refetchInterval: 30_000 });
}

export function useFinanceInvoices(direction: InvoiceDirection, query: FinanceInvoiceQuery, enabled = true) {
  return useScopedNumericQuery(['finance', 'invoices', direction, query], invoiceRead[direction], async (options) => {
    const result = await financeCommandCenterApi.listInvoices(direction, query, options);
    if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load invoice register');
    return result.data;
  }, { enabled, refetchInterval: 30_000 });
}

export function useFinanceInvoice(direction: InvoiceDirection, id: string | null, asOf?: string) {
  return useScopedNumericQuery(['finance', 'invoice', direction, id, asOf], invoiceRead[direction], async (options) => {
    const result = await financeCommandCenterApi.getInvoice(direction, id!, asOf, options);
    if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load invoice details');
    return result.data;
  }, { enabled: Boolean(id) });
}

export function useFinanceMarginTrend(months: number, enabled = true) {
  const budgetAccess = useSessionScopeFence(PERMISSIONS.FINANCE_BUDGETS_READ).enabled;
  return useScopedNumericQuery(['finance', 'recorded-wip-trend', months], PERMISSIONS.FINANCE_WIP_SNAPSHOTS_READ,
    (options) => analyticsClient.getMarginTrend(months, options), { enabled: enabled && budgetAccess, staleTime: 60_000 });
}
